import { Loading } from '@cibo/profile'
import { useProgramModel } from '@cibo/programs'
import { TaskMarkdown } from '@cibo/programs/src/features/Tasks/TaskMarkdown'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import HourglassTop from '@mui/icons-material/HourglassTop'
import RouteOutlined from '@mui/icons-material/RouteOutlined'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useProgramEngagementContext } from '../../features/ProgramEngagement'
import { EngagementRouteParams } from '../../navigation/types/engagementRoutes'

export const ProgramEngagementStepStatus = () => {
  const { stepId } = useParams<EngagementRouteParams>()
  const { engagement, ownerName, userRole, basePath } = useProgramEngagementContext()
  const stepStatus = engagement.data?.getStepStatus(stepId)
  const { t } = useTranslation('@cibo/impact/ProgramEngagementStepStatus')
  const program = useProgramModel({ programId: engagement.data?.programId })
  const content = program.data?.stepContent(stepId)

  const nextPath = useMemo(() => {
    if (!stepId) return
    const nextStepId = program.data?.nextStepId(stepId)
    if (nextStepId) {
      const nextTaskId = program.data?.visibleStepTasks(nextStepId)?.[0]
      return !!nextTaskId ? `../../${nextStepId}/${nextTaskId}` : undefined
    }
  }, [stepId, program.dataUpdatedAt])

  const variant = useMemo(() => {
    switch (content?.content.status?.complete.eligible.variant) {
      case 'keepGoing': {
        return (
          <>
            <CheckCircle color="success" fontSize="inherit" sx={{ fontSize: 60 }} />
            <Typography variant="overline">{t('keepGoing')}</Typography>
          </>
        )
      }
      case 'waiting': {
        return (
          <>
            <HourglassTop
              color="warning"
              fontSize="inherit"
              sx={{
                fontSize: 48,
                backgroundColor: 'warning.main',
                color: 'warning.contrastText',
                borderRadius: '50%',
                padding: 0.7,
              }}
            />
            <Typography variant="overline">{t('waiting')}</Typography>
          </>
        )
      }
      case 'nextUp': {
        return (
          <>
            <RouteOutlined
              color="success"
              fontSize="inherit"
              sx={{
                fontSize: 48,
                backgroundColor: 'success.main',
                color: 'success.contrastText',
                borderRadius: '50%',
                padding: 0.7,
              }}
            />
            <Typography variant="overline">{t('nextUp')}</Typography>
          </>
        )
      }
      default:
        return null
    }
  }, [content?.content.status?.complete.eligible.variant])

  if (program.isLoading) {
    return <Loading />
  }

  if (!!nextPath && !content?.content.status) {
    return <Navigate to={nextPath} replace />
  }

  return (
    <Stack alignItems="center" textAlign="center" flex={1} spacing={3}>
      {stepStatus?.status.eligibility?.eligible ? (
        <>
          <Stack alignItems="center" textAlign="center" flex={1} spacing={1.5}>
            {!!variant && (
              <Stack alignItems="center" textAlign="center" spacing={2}>
                {variant}
              </Stack>
            )}
            <Box maxWidth={600}>
              <TaskMarkdown color="text.secondary">
                {content?.content.status?.complete.eligible.markdown[userRole].replace(
                  /\{\{name\}\}/g,
                  ownerName
                )}
              </TaskMarkdown>
            </Box>
          </Stack>
          {content?.content.status?.complete.eligible.variant !== 'waiting' && (
            <Button component={Link} to={nextPath ?? basePath} variant="contained" replace>
              {content?.content.status?.complete.eligible.cta ?? t('continue')}
            </Button>
          )}
        </>
      ) : (
        <>
          <Stack alignItems="center" textAlign="center" flex={1} spacing={1.5}>
            <Stack alignItems="center" textAlign="center" spacing={2}>
              <Cancel color="error" fontSize="inherit" sx={{ fontSize: 48 }} />
              <Typography variant="overline">{t('ineligible')}</Typography>
            </Stack>
            <Box maxWidth={600}>
              <TaskMarkdown>
                {content?.content.status?.complete.ineligible.markdown[userRole]?.replace(
                  /\{\{name\}\}/g,
                  ownerName
                )}
              </TaskMarkdown>
            </Box>
          </Stack>
        </>
      )}
    </Stack>
  )
}
